import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useExpedientes } from '../lib/hooks';
import { RIESGO_LABEL, type Expediente } from '../lib/phase1';
import {
  Button,
  Card,
  EmptyState,
  Pill,
  cn,
  friendlyEstado,
} from '../components/ui';
import { riesgoTone } from '../components/compliance';
import { ListChecks, Upload, Download, UserCheck, UserX } from 'lucide-react';

const MAX_NAMES = 500;

/** One pasted name and the registered client it matched (if any). */
interface MatchRow {
  input: string;
  client: Expediente | null;
}

/** Accent-fold + lowercase + strip punctuation, so "Empresa, S.A." and
 * "empresa sa" compare equal. */
const normName = (s: string): string =>
  s
    .normalize('NFD')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const tokenize = (s: string): string[] => normName(s).split(' ').filter(Boolean);

// Corporate suffixes / connectors carry no identifying signal — drop them before
// the containment check so a bare "S.A." or "del" can't match a client.
const STOPWORDS = new Set(['sa', 'srl', 'de', 'del', 'la', 'las', 'los', 'y', 'e']);
const meaningful = (tokens: string[]): string[] => tokens.filter((t) => !STOPWORDS.has(t));

/**
 * A pasted name matches a client when the normalized names are equal, or one
 * meaningful token set contains the other — so "Carlos Mendoza" finds "Carlos
 * Mendoza Him", while a lone name or bare suffix ("María", "S.A.") does not.
 * Partial matching needs ≥2 meaningful tokens to avoid single-token/suffix
 * false positives. First match wins (the demo cartera is small).
 */
function matchClient(input: string, clients: Expediente[]): Expediente | null {
  const qn = normName(input);
  if (!qn) return null;
  const exact = clients.find((c) => normName(c.name) === qn);
  if (exact) return exact;
  const qm = meaningful(qn.split(' '));
  if (qm.length < 2) return null;
  return (
    clients.find((c) => {
      const cm = meaningful(tokenize(c.name));
      return qm.every((t) => cm.includes(t)) || (cm.length >= 2 && cm.every((t) => qm.includes(t)));
    }) ?? null
  );
}

/** Parse pasted/uploaded text into a clean, de-duplicated name list — one name
 * per line, whole line. We deliberately do NOT split on commas: Panamanian legal
 * names almost always carry a corporate suffix after a comma ("Empresa, S.A."),
 * so a comma is part of the name, not a delimiter. */
function parseNames(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of raw.split(/\r?\n/)) {
    const name = line.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

function toCsv(rows: MatchRow[]): string {
  const header = ['nombre_ingresado', 'resultado', 'cliente', 'expediente', 'riesgo', 'estado'];
  const body = rows.map((r) =>
    r.client
      ? [r.input, 'Es cliente', r.client.name, r.client.id, RIESGO_LABEL[r.client.riesgo], r.client.estado]
      : [r.input, 'No es cliente', '', '', '', '']
  );
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return [header, ...body].map((row) => row.map(esc).join(',')).join('\r\n');
}

/**
 * Cuerpo de «Validación masiva» — vive como pestaña dentro de Alertas y Casos.
 * Pega/carga una lista de nombres y descubre cuáles ya son clientes de la cartera.
 */
export function ScreeningBatchPanel() {
  const { data: clients, isLoading } = useExpedientes();
  const [raw, setRaw] = useState('');
  const [rows, setRows] = useState<MatchRow[] | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const parsed = useMemo(() => parseNames(raw), [raw]);
  const capped = parsed.slice(0, MAX_NAMES);
  const overflow = parsed.length - capped.length;

  function run() {
    const base = clients ?? [];
    setRows(capped.map((input) => ({ input, client: matchClient(input, base) })));
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setRaw(String(reader.result ?? ''));
    reader.readAsText(file);
    e.target.value = ''; // allow re-selecting the same file
  }

  function downloadCsv() {
    if (!rows) return;
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cartera-cotejo-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <Card data-tour="screening-form" className="mb-6">
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-soft">
          Nombres a cotejar (uno por línea)
        </label>
        <textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={8}
          placeholder={'Juan Pérez González\nEmpresa Ejemplo, S.A.\nMaría López Castillo'}
          className="w-full resize-y rounded-lg border border-line-strong bg-canvas px-3 py-2 font-mono text-sm text-ink placeholder:text-ink-faint focus:border-primary focus:bg-panel focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <p className="mt-1.5 text-xs text-ink-faint">
          Una persona o empresa por línea (la línea completa es el nombre). Máximo {MAX_NAMES}{' '}
          nombres por lote. Se cotejan contra su cartera de clientes registrados.
          {parsed.length > MAX_NAMES ? (
            <span className="ml-1 font-semibold text-tone-red">
              · {capped.length}/{MAX_NAMES} (se excluyen {overflow})
            </span>
          ) : parsed.length > 0 ? (
            <span className="ml-1 font-semibold text-primary">
              · {parsed.length} {parsed.length === 1 ? 'nombre' : 'nombres'}
            </span>
          ) : null}
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button onClick={run} disabled={capped.length === 0 || isLoading}>
            <ListChecks className="h-4 w-4" strokeWidth={2} />
            {isLoading
              ? 'Cargando cartera…'
              : `Cotejar ${capped.length || ''} ${capped.length === 1 ? 'nombre' : 'nombres'}`.trim()}
          </Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()}>
            <Upload className="h-4 w-4" strokeWidth={2} />
            Cargar archivo (.csv / .txt)
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            onChange={onFile}
            className="hidden"
          />
          {overflow > 0 && (
            <span className="text-xs font-semibold text-tone-red">
              Se cotejarán los primeros {MAX_NAMES}; {overflow} quedaron fuera del lote.
            </span>
          )}
        </div>
      </Card>

      {rows ? (
        <ResultsView rows={rows} onDownload={downloadCsv} />
      ) : (
        <Card>
          <EmptyState
            icon={<ListChecks className="h-8 w-8" strokeWidth={1.5} />}
            title="Sin resultados todavía"
            hint="Pegue o cargue una lista de nombres y presione «Cotejar» para ver cuáles ya son clientes registrados en su cartera."
          />
        </Card>
      )}
    </div>
  );
}

function ClientCell({ client }: { client: Expediente }) {
  const fe = friendlyEstado(client.estado);
  return (
    <div className="min-w-0">
      <Link
        to={`/expedientes/${client.id}`}
        className="font-bold text-primary hover:text-primary-dark hover:underline"
      >
        {client.name}
      </Link>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <span className="font-mono text-[10.5px] text-ink-faint">{client.id}</span>
        <Pill tone={fe.tone}>{fe.label}</Pill>
        {client.riesgo !== 'sin' && (
          <Pill tone={riesgoTone(client.riesgo)}>Riesgo {RIESGO_LABEL[client.riesgo]}</Pill>
        )}
        {client.pep && <Pill tone="red">PEP</Pill>}
      </div>
    </div>
  );
}

function ResultsView({ rows, onDownload }: { rows: MatchRow[]; onDownload: () => void }) {
  const matches = rows.filter((r) => r.client).length;
  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone="slate">{rows.length} cotejados</Pill>
          <Pill tone={matches > 0 ? 'amber' : 'green'}>
            {matches} en la cartera
          </Pill>
        </div>
        <Button variant="outline" onClick={onDownload}>
          <Download className="h-4 w-4" strokeWidth={2} />
          Descargar CSV
        </Button>
      </div>

      {/* mobile: stacked cards */}
      <div className="grid gap-2.5 sm:hidden">
        {rows.map((r, i) => (
          <div
            key={i}
            className={cn(
              'rounded-2xl border bg-panel p-4 shadow-card',
              r.client ? 'border-tone-amber/40' : 'border-line'
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="font-bold text-ink">{r.input}</span>
              <Pill tone={r.client ? 'amber' : 'slate'} className="shrink-0">
                {r.client ? 'Es cliente' : 'No es cliente'}
              </Pill>
            </div>
            {r.client && (
              <div className="mt-2">
                <ClientCell client={r.client} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* desktop: table */}
      <Card className="hidden overflow-x-auto p-0 sm:block">
        <table className="w-full min-w-[44rem] text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              {['Nombre ingresado', 'Resultado', 'Cliente en la cartera'].map((h) => (
                <th
                  key={h}
                  className="px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-faint"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-line last:border-0">
                <td className="px-5 py-3 font-bold align-top">{r.input}</td>
                <td className="px-5 py-3 align-top">
                  <span className="inline-flex items-center gap-1.5">
                    {r.client ? (
                      <UserCheck className="h-4 w-4 text-tone-amber" strokeWidth={2} />
                    ) : (
                      <UserX className="h-4 w-4 text-ink-faint" strokeWidth={2} />
                    )}
                    <Pill tone={r.client ? 'amber' : 'slate'}>
                      {r.client ? 'Es cliente' : 'No es cliente'}
                    </Pill>
                  </span>
                </td>
                <td className="px-5 py-3 align-top">
                  {r.client ? (
                    <ClientCell client={r.client} />
                  ) : (
                    <span className="text-ink-faint">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
